import { getWorkoutSetColumns } from './workoutSetColumns';

export function buildWorkoutCoachScopeOptions(workoutExercises = [], exercises = []) {
  const active = workoutExercises.filter(item => item.status !== 'removed');
  const nameFor = item => {
    const catalogExercise = exercises.find(exercise => String(exercise.id || exercise._id) === String(item.exercise_id));
    return catalogExercise?.name || item.name || String(item.exercise_id || 'Exercise').replace(/_/g, ' ');
  };
  const options = [];
  const seenSupersets = new Set();
  for (const item of active) {
    if (item.superset_group == null) {
      options.push({ id: String(item._id), memberIds: [String(item._id)], label: nameFor(item) });
      continue;
    }
    const groupKey = String(item.superset_group);
    if (seenSupersets.has(groupKey)) continue;
    seenSupersets.add(groupKey);
    const members = active.filter(candidate => String(candidate.superset_group) === groupKey);
    options.push({
      id: String(members[0]._id),
      memberIds: members.map(member => String(member._id)),
      label: `Superset #${item.superset_group} · ${members.map(nameFor).join(' + ')}`,
    });
  }
  return options;
}

export function workoutCoachScopeAnchorId(workoutExercises = [], workoutExerciseId = '') {
  const targetId = String(workoutExerciseId || '');
  const target = workoutExercises.find(item => String(item?._id) === targetId);
  if (!target || target.superset_group === null || target.superset_group === undefined) return targetId;
  return String(workoutExercises.find(item => (
    item?.status !== 'removed' && String(item.superset_group) === String(target.superset_group)
  ))?._id || targetId);
}

export function workoutCoachScopeLabel(scope = {}) {
  if (scope.type === 'exercise') return scope.exercise_name || 'Exercise';
  if (scope.type === 'superset') {
    const label = scope.label || (scope.superset_group != null ? `Superset #${scope.superset_group}` : 'Superset');
    return scope.source_exercise_name ? `${label} · ${scope.source_exercise_name}` : label;
  }
  return 'Entire workout';
}

export function workoutCoachEventKey(event = {}) {
  return String(event._id || event.idempotency_key || `${event.sequence || 0}-${event.type || 'event'}`);
}

function setEventText(payload = {}, exerciseMeta) {
  const setNumber = (Number(payload.set_index) || 0) + 1;
  const parts = [];
  // Prefer server-projected tracking metadata. Without positively known
  // metadata retain all positive legacy values, even equal reps/seconds.
  const projected = Array.isArray(payload.tracked_fields) && payload.tracked_fields.length > 0
    ? payload.tracked_fields : null;
  const knownCatalog = payload.metric_metadata !== 'unavailable' && exerciseMeta && (exerciseMeta.trackingFields?.length > 0
    || ['strength', 'powerlifting', 'olympic weightlifting', 'strongman', 'cardio', 'sport', 'static', 'stretching'].includes(String(exerciseMeta.category || '').toLowerCase()));
  const fields = projected || (knownCatalog ? getWorkoutSetColumns(exerciseMeta).map(column => column.field) : null);
  const supports = field => !fields || fields.includes(field);
  if (supports('weight') && Number(payload.weight) > 0) parts.push(String(payload.weight));
  if (supports('repetitions') && Number(payload.repetitions) > 0) parts.push(`${payload.repetitions} reps`);
  if (supports('duration') && Number(payload.duration) > 0) parts.push(`${Math.round(payload.duration / 1000)}s`);
  if (supports('distance') && Number(payload.distance) > 0) parts.push(`${payload.distance} distance`);
  return `Set ${setNumber}${parts.length ? ` · ${parts.join(' × ')}` : ' completed'}`;
}

export function presentWorkoutCoachEvent(event = {}, exerciseMeta) {
  const scopeLabel = workoutCoachScopeLabel(event.scope);
  const payload = event.payload || {};
  if (event.type === 'workout_resumed') return { kind: 'lifecycle', text: 'Workout resumed', scopeLabel };
  if (event.type === 'workout_started') return { kind: 'lifecycle', text: 'Workout started', scopeLabel };
  if (event.type === 'workout_stopped') return { kind: 'lifecycle', text: 'Workout stopped', scopeLabel };
  if (event.type === 'workout_reopened') return { kind: 'lifecycle', text: 'Workout reopened', scopeLabel };
  if (event.type === 'workout_completed') return { kind: 'lifecycle', text: 'Workout completed', scopeLabel };
  if (event.type === 'set_completed') return { kind: 'user-set', side: 'user', text: setEventText(payload, exerciseMeta), scopeLabel };
  if (event.type === 'user_message') {
    return { kind: 'user', text: payload.message || (payload.attachments?.length ? 'Photo attached' : 'User message'), scopeLabel, attachments: payload.attachments || [] };
  }
  if (event.type === 'coach_message') {
    return { kind: 'coach', text: coachMessageText(event) || 'Coach message', scopeLabel };
  }
  if (event.type === 'coach_reaction') {
    return { kind: 'reaction', text: payload.reaction?.label || 'Coach reaction', scopeLabel, reaction: payload.reaction || null };
  }
  if (event.type === 'proposed_action') return { kind: 'proposal', text: payload.description || 'Coach proposed a change', scopeLabel };
  if (event.type === 'action_approved') return { kind: 'resolution approved', text: `Applied: ${payload.description || 'Coach change'}`, scopeLabel };
  if (event.type === 'action_rejected') return { kind: 'resolution rejected', text: `Dismissed: ${payload.description || 'Coach change'}`, scopeLabel };
  if (event.type === 'action_superseded') return payload.reason === 'completed_set'
    ? { kind: 'resolution superseded', text: 'Coach skipped this suggestion because the set was already completed. Your logged set is unchanged.', scopeLabel }
    : payload.reason === 'stale_set'
      ? { kind: 'resolution superseded', text: 'Coach skipped this suggestion because the target set is unavailable. No workout sets were changed.', scopeLabel }
      : payload.reason === 'deleted_set'
      ? { kind: 'resolution superseded', text: 'Coach skipped this suggestion because the set was removed. Your workout sets are unchanged.', scopeLabel }
      : { kind: 'resolution superseded', text: `Superseded: ${payload.description || 'Coach change'}`, scopeLabel };
  return { kind: 'event', text: event.type || 'Workout event', scopeLabel };
}

function targetWorkoutExerciseId(scope = {}) {
  if (scope.type === 'exercise') return scope.workout_exercise_id || null;
  if (scope.type === 'superset') return scope.source_workout_exercise_id || scope.workout_exercise_ids?.[0] || null;
  return null;
}

export function derivePendingWorkoutCoachActions(events = []) {
  const ordered = [...events].sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0));
  const proposals = new Map();
  const resolved = new Set();
  for (const event of ordered) {
    const actionId = event.payload?.action_id;
    if (!actionId) continue;
    if (event.type === 'proposed_action') proposals.set(actionId, event);
    if (['action_approved', 'action_rejected', 'action_superseded'].includes(event.type)) resolved.add(actionId);
  }
  return [...proposals.entries()]
    .filter(([actionId]) => !resolved.has(actionId))
    .map(([, event]) => ({
      action: { ...event.payload, status: 'pending' },
      // Discussion scope is provenance, not necessarily the set being changed.
      // Keep non-set tools source-scoped; the server validates set targets
      // against the unique persisted proposal in the authorized workout.
      workoutExerciseId: ['modify_set_weight', 'modify_set_reps', 'add_set', 'remove_set'].includes(event.payload?.tool_name)
        ? (event.payload?.arguments?.workout_exercise_id || targetWorkoutExerciseId(event.scope))
        : targetWorkoutExerciseId(event.scope),
      scopeLabel: workoutCoachScopeLabel(event.scope),
    }))
    .filter(item => item.workoutExerciseId);
}

function coachMessageText(event = {}) {
  return [event.payload?.message, event.payload?.question]
    .find(text => typeof text === 'string' && text.trim().length > 0) || '';
}

export function shouldRenderWorkoutCoachDiscussionEvent(event = {}) {
  if (event.type === 'coach_message') return Boolean(coachMessageText(event));
  if (event.type === 'action_superseded' && ['completed_set', 'deleted_set', 'stale_set'].includes(event.payload?.reason)) return true;
  return !['coach_reaction', 'proposed_action', 'action_approved', 'action_rejected', 'action_superseded'].includes(event.type);
}

export function buildWorkoutCoachDiscussion(events = [], exerciseMetadataById = {}) {
  const reactionsByTarget = new Map(events
    .filter(event => event.type === 'coach_reaction' && event.payload?.target_event_key)
    .map(event => [event.payload.target_event_key, event.payload.reaction || null]));
  return events
    .filter(shouldRenderWorkoutCoachDiscussionEvent)
    .map(event => ({
      event,
      presentation: {
        ...presentWorkoutCoachEvent(event, exerciseMetadataById[event.scope?.source_workout_exercise_id || event.scope?.workout_exercise_id]),
        ...(reactionsByTarget.has(event.idempotency_key)
          ? { reaction: reactionsByTarget.get(event.idempotency_key) }
          : {}),
      },
    }));
}

export function latestWorkoutCoachMessage(events = []) {
  const coachMessages = events
    .filter(event => event.type === 'coach_message')
    .sort((a, b) => (Number(b.sequence) || 0) - (Number(a.sequence) || 0));
  const latest = coachMessages[0]?.payload || {};
  return latest.message || latest.question || '';
}

export function latestWorkoutCoachPresence(events = []) {
  const ordered = [...events].sort((a, b) => (Number(b.sequence) || 0) - (Number(a.sequence) || 0));
  const reactionEvent = ordered.find(event => event.type === 'coach_reaction' && event.payload?.reaction);
  const newestMessage = ordered.find(event => event.type === 'coach_message' && coachMessageText(event));
  const messagePresence = messageEvent => {
    return {
      reaction: null,
      text: coachMessageText(messageEvent),
      scopeLabel: workoutCoachScopeLabel(messageEvent?.scope),
      key: messageEvent?._id || messageEvent?.idempotency_key || `${messageEvent?.sequence || 0}:${coachMessageText(messageEvent)}`,
      focusEventKey: messageEvent ? workoutCoachEventKey(messageEvent) : '',
      focusWorkoutExerciseId: null,
    };
  };
  if (!reactionEvent) return messagePresence(newestMessage);
  const targetKey = reactionEvent.payload.target_event_key;
  if (
    Number(newestMessage?.sequence) > Number(reactionEvent.sequence)
    && newestMessage?.payload?.reaction_target_key !== targetKey
  ) return messagePresence(newestMessage);
  const pairedMessage = ordered.find(event => (
    event.type === 'coach_message' && coachMessageText(event) && event.payload?.reaction_target_key === targetKey
  ));
  const targetEvent = events.find(event => event.idempotency_key === targetKey);
  const text = coachMessageText(pairedMessage)
    || (reactionEvent.payload.target_type === 'user_message'
      ? 'Reacted to your message'
      : `Reacted to ${workoutCoachScopeLabel(reactionEvent.scope)} set`);
  return {
    reaction: reactionEvent.payload.reaction,
    text,
    scopeLabel: workoutCoachScopeLabel(reactionEvent.scope || targetEvent?.scope),
    key: reactionEvent._id || reactionEvent.idempotency_key || `${reactionEvent.sequence || 0}:${targetKey}:${reactionEvent.payload.reaction.key}`,
    focusEventKey: targetEvent ? workoutCoachEventKey(targetEvent) : targetKey,
    focusWorkoutExerciseId: targetWorkoutExerciseId(targetEvent?.scope || reactionEvent.scope),
  };
}

export function shouldShowWorkoutCoachDock({ readOnly = false, workoutStatus, isTemplate = false }) {
  return !readOnly && !isTemplate && workoutStatus === 'ongoing';
}
